import { useMemo, useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2, AlertCircle } from 'lucide-react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DropSelect,
  Input,
  Separator,
  Skeleton,
  FormSection,
  toast,
  type OptionSelect,
} from '@zawarski/eti-ui';
import { personnelSchema, type PersonnelFormSchema } from '@/common/schemas/personnel.schema';
import { useAppSelector } from '@/store/hooks';
import {
  selectActiveCountyId,
  selectUserId,
  selectIncidentGatheringId,
} from '@/store/auth/auth.selector';
import { useGetPalmettoGroupsQuery } from '@/services/palmettoGroupApi';
import {
  useSavePersonnelMutation,
  useDeletePersonnelMutation,
  type PersonnelPayload,
  type PersonnelResponse,
} from '@/services/personnelApi';
import { cn, formatPhoneInput, getGroupDisplayName } from '@/lib/utils';

interface PersonnelModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaveSuccess?: () => void;
  mode?: 'add' | 'edit';
  personnel?: PersonnelResponse | null;
  isLoading?: boolean;
}

export function PersonnelModal({
  open,
  onOpenChange,
  onSaveSuccess,
  mode = 'add',
  personnel,
  isLoading = false,
}: PersonnelModalProps) {
  const userId = useAppSelector(selectUserId);
  const activeGroup = useAppSelector(selectActiveCountyId);
  const gatheringId = useAppSelector(selectIncidentGatheringId);
  const [saveError, setSaveError] = useState<string | null>(null);
  // API
  const { data: palmettoGroups = [] } = useGetPalmettoGroupsQuery();
  const [savePersonnel, { isLoading: saving }] = useSavePersonnelMutation();
  const [deletePersonnel, { isLoading: deleting }] = useDeletePersonnelMutation();

  const organizationOptions = useMemo<OptionSelect[]>(
    () =>
      palmettoGroups
        .filter((group) => group.pvGroupID >= 2 && group.pvGroupID <= 48)
        .map((group) => ({
          label: getGroupDisplayName(group),
          value: String(group.pvGroupID),
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [palmettoGroups]
  );

  const { control, handleSubmit, reset, formState } = useForm<PersonnelFormSchema>({
    mode: 'onChange',
    resolver: zodResolver(personnelSchema),
    defaultValues: {
      id: '',
      name: '',
      position: '',
      email: '',
      phone: '',
      organization: String(activeGroup),
    },
  });

  useEffect(() => {
    if (formState && formState.errors && Object.keys(formState.errors).length) {
      console.log('🚀 formState errors >>> ', formState.errors);
    }
  }, [formState, formState?.errors]);

  // Reset form with personnel data when editing
  // Note: Component should be keyed by mode/personnel?.id in parent to reset state
  useEffect(() => {
    if (mode === 'edit' && personnel) {
      reset({
        id: personnel.id,
        name: personnel.name,
        position: personnel.position || '',
        email: personnel.email || '',
        phone: personnel.phone_number || '',
        organization: personnel.organization || '',
      });
    } else if (mode === 'add') {
      reset({
        id: '',
        name: '',
        position: '',
        email: '',
        phone: '',
        organization: String(activeGroup),
      });
    }
  }, [mode, personnel, reset, activeGroup]);

  const handleFormSubmit = async (data: PersonnelFormSchema) => {
    console.log('🚀 gatheringId >>> ', gatheringId);
    console.log('🚀 activeGroup >>> ', activeGroup);
    console.log('🚀 userId >>> ', userId);
    if (!gatheringId || !activeGroup || !userId) return;
    setSaveError(null);

    const savePayload: PersonnelPayload = {
      // Include ID for edit mode (PUT request)
      ...(mode === 'edit' && personnel?.id ? { id: personnel.id } : {}),
      incident_id: gatheringId,
      group_id: activeGroup,
      name: data.name,
      position: data.position || '',
      email: data.email,
      phone_number: data.phone || '',
      organization: data.organization,
      user_id: String(userId),
    };

    try {
      await savePersonnel(savePayload).unwrap();
      toast.success(mode === 'add' ? 'Personnel added' : 'Personnel updated');
      reset();
      onSaveSuccess?.();
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Unable to save personnel. Please try again.';
      setSaveError(errorMessage);
      console.error('Save personnel error:', error);
    }
  };

  const handleCancel = () => {
    reset();
    setSaveError(null);
    onOpenChange(false);
  };

  const handleDelete = async () => {
    if (!personnel?.id) return;
    setSaveError(null);

    try {
      await deletePersonnel(personnel.id).unwrap();
      toast.success('Personnel deleted');
      reset();
      onSaveSuccess?.();
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Unable to delete personnel. Please try again.';
      setSaveError(errorMessage);
      console.error('Delete personnel error:', error);
    }
  };

  const isFormDisabled = isLoading || saving || deleting;

  return (
    <Dialog open={open} onOpenChange={!saving && !deleting ? onOpenChange : undefined}>
      <DialogContent className='relative p-0! sm:max-w-[500px]'>
        {/* Loading Overlay */}
        {isLoading && (
          <div className='bg-background/80 absolute inset-0 z-50 flex items-center justify-center rounded-lg'>
            <div className='flex flex-col items-center gap-2'>
              <Loader2 className='text-primary h-8 w-8 animate-spin' />
              <span className='text-muted-foreground text-sm'>Loading personnel...</span>
            </div>
          </div>
        )}

        <DialogHeader className='items-start px-4 pt-4'>
          <DialogTitle>{mode === 'edit' ? 'Edit personnel' : 'Add personnel'}</DialogTitle>
          <DialogDescription>
            {mode === 'edit' ? 'Update personnel details below' : 'Add personnel details below'}
          </DialogDescription>
        </DialogHeader>
        <Separator />
        <div className='p-4'>
          <FormSection title='Personnel details' uppercase>
            {isLoading ? (
              <Skeleton />
            ) : (
              <div className='grid w-full grid-cols-1 gap-4 p-4 md:grid-cols-2'>
                {/* Personnel Details Section */}
                <Controller
                  control={control}
                  name='name'
                  render={({ field: { onChange, value }, fieldState: { error } }) => (
                    <Input
                      required
                      label='Name'
                      placeholder='Enter name'
                      value={value}
                      onChange={onChange}
                      error={!!error}
                      helperText={error?.message}
                    />
                  )}
                />
                <Controller
                  control={control}
                  name='position'
                  render={({ field: { onChange, value }, fieldState: { error } }) => (
                    <Input
                      label='Position'
                      placeholder='Enter position'
                      value={value}
                      onChange={onChange}
                      error={!!error}
                      helperText={error?.message}
                    />
                  )}
                />
                <span className='text-muted-foreground flex items-center gap-2 text-sm font-medium md:col-span-2'>
                  <span>Contact details</span>
                  <span className='border-muted-foreground/20 flex-1 border-b' />
                </span>
                {/* Contact Details Section */}
                <span className='gap-4 md:col-span-2'>
                  <span className='flex flex-col gap-4 md:grid md:grid-cols-3'>
                    <Controller
                      control={control}
                      name='email'
                      render={({ field: { onChange, value }, fieldState: { error } }) => (
                        <Input
                          type='email'
                          label='Email address'
                          placeholder='Enter email address'
                          value={value}
                          onChange={onChange}
                          error={!!error}
                          helperText={error?.message}
                        />
                      )}
                    />
                    <Controller
                      control={control}
                      name='phone'
                      render={({ field: { onChange, value }, fieldState: { error } }) => (
                        <Input
                          label='Phone number'
                          placeholder='888-888-8888'
                          value={formatPhoneInput(value)}
                          onChange={(e) => onChange(formatPhoneInput(e.target.value))}
                          error={!!error}
                          helperText={error?.message}
                        />
                      )}
                    />
                    <Controller
                      control={control}
                      name='organization'
                      render={({ field: { onChange, value }, fieldState: { error } }) => (
                        <DropSelect
                          label='Organization/County'
                          // required
                          options={organizationOptions}
                          value={value || ''}
                          onChange={onChange}
                          error={!!error}
                          helperText={error && error?.message ? error.message : ''}
                        />
                      )}
                    />
                  </span>
                </span>
              </div>
            )}
          </FormSection>
        </div>
        {/* Error Message */}
        {saveError && (
          <div className='bg-destructive/10 text-destructive mx-4 mb-2 flex items-center gap-2 rounded-md p-3 text-sm'>
            <AlertCircle className='h-4 w-4 shrink-0' />
            <span>{saveError}</span>
          </div>
        )}
        <Separator />
        <DialogFooter
          className={cn(
            'w-full! flex-row! px-4 pb-4',
            mode === 'edit' ? 'justify-between!' : 'justify-end!'
          )}>
          <div>
            {mode === 'edit' && (
              <Button
                variant='destructive'
                loading={deleting}
                disabled={isFormDisabled}
                onClick={handleDelete}>
                Delete
              </Button>
            )}
          </div>
          <div className='flex gap-2'>
            <Button variant='outline' disabled={isFormDisabled} onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              loading={saving}
              onClick={!isFormDisabled ? handleSubmit(handleFormSubmit) : undefined}>
              Save
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
